import DigiobcanApi, { type UserDetail } from '../../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../../components/ConfirmationDialog';
import FormAutocompleteField from '../../../../components/form/FormAutocompleteField';
import {
  searchUsers,
  type UserCandidate,
} from '../../../../features/queries/searchUsers';
import { shortId } from '../../../../lib/ids';
import { useInfiniteQ, useM } from '../../../../lib/query';
import { useLogger } from '../../../../providers/LoggerProvider';
import {
  __,
  requiredArray,
  toastError,
  toastSuccess,
  useDebounced,
} from '../../../../utils';
import React from 'react';
import { Form } from 'react-final-form';

const LIMIT = 20;

/**
 * Ponuka má vždy aspoň tolko možností, inak sa doťahuje ďalšia stránka.
 * Bez toho môže celá stránka vypadnúť na vylúčení už priradených a listbox
 * sa nedá doscrollovať, teda ani dopĺňať.
 */
const MIN_OPTIONS = 10;

/**
 * Kandidát má presne jeden identifikátor: `client_acc_id`, ak sa človek do
 * appky už prihlásil, inak `acc_id`. Do formulára ide ten, ktorý prišiel,
 * a s ním sa aj odošle - `acc_id` si digiobcan-api pri priradení premení na
 * identifikátor v aplikácii sám.
 */
const candidateKey = (user: UserCandidate) =>
  user.client_acc_id || user.acc_id || '';

const userOption = (user: UserCandidate) => {
  const name = [user.name, user.surname].filter(Boolean).join(' ');

  return {
    label: `${name}${name ? ' - ' : ''}${shortId(candidateKey(user))}`,
    value: candidateKey(user),
  };
};

type Props = {
  categoryId: string;
  open?: boolean;
  onClose: () => void;
  usedUserIds?: string[];
  onSuccess?: (users: UserDetail[]) => void;
};

const AddCategoryUserDialog: React.FC<Props> = ({
  categoryId,
  open,
  onClose,
  usedUserIds,
  onSuccess,
}) => {
  const { logger } = useLogger();

  const [autocompleteValue, setAutocompleteValue] = React.useState('');

  // Hľadanie vracia vždy len účty pre aktuálny vstup, takže po ďalšom hľadaní
  // by už raz vybraný účet medzi `options` nebol: značka by z poľa zmizla
  // a mutácia by k jeho ID nenašla meno. Preto si každý videný účet pamätáme.
  const [seenUsers, setSeenUsers] = React.useState<
    Record<string, UserCandidate>
  >({});

  const autocompleteValueDebounced = useDebounced(autocompleteValue, 300);

  // Hľadá sa od prvého znaku a hľadá server; prázdny vstup znamená celý zoznam
  // kandidátov, ktorý sa dopĺňa na scroll. Klientské filtrovanie je preto
  // vypnuté natrvalo - filtrovalo by len práve načítané stránky.
  const search = autocompleteValueDebounced.trim();

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQ(
    searchUsers,
    { q: search || undefined, limit: LIMIT },
    {
      enabled: open,
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        !lastPage || lastPage.users.length < LIMIT
          ? undefined
          : allPages.reduce((count, page) => count + page.users.length, 0),
    },
  );

  const candidates = React.useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.users)
        .filter((user) => !usedUserIds?.includes(user.client_acc_id || '')),
    [data, usedUserIds],
  );

  React.useEffect(() => {
    setSeenUsers((prev) => {
      const next = { ...prev };
      let added = false;
      candidates.forEach((user) => {
        const key = candidateKey(user);
        if (key && !next[key]) {
          next[key] = user;
          added = true;
        }
      });
      return added ? next : prev;
    });
  }, [candidates]);

  // Už priradení sa vylučujú až tu, teda po limite servera, takže stránka môže
  // vyjsť takmer prázdna. Dosýtime ju, kým je čo.
  React.useEffect(() => {
    if (
      open &&
      hasNextPage &&
      !isFetchingNextPage &&
      !isLoading &&
      candidates.length < MIN_OPTIONS
    ) {
      fetchNextPage();
    }
  }, [
    open,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    candidates.length,
    fetchNextPage,
  ]);

  const addReportCategoryUserMutation = useM(
    async (keys: string[]) => {
      const users = keys
        .map((key) => seenUsers[key])
        .filter((user): user is UserCandidate => !!user)
        .map(({ client_acc_id, acc_id, name, surname }) => ({
          ...(client_acc_id ? { client_acc_id } : { acc_id }),
          ...(name && { name }),
          ...(surname && { surname }),
        }));

      return await DigiobcanApi.addReportCategoryUsers(categoryId, {}, users);
    },
    {
      onSuccess: (category) => {
        const userNames = category.users
          .map((u) => [u.name, u.surname].filter(Boolean).join(' ') || u.id)
          .join(', ');
        toastSuccess(
          __(
            `Kategória ${category.name} bola priradená používateľom: ${userNames}`,
          ),
        );
        // Identifikátor účtu, ktorý sa registroval až týmto volaním, pozná len
        // odpoveď - preto sa riadky do tabuľky skladajú z nej, nie z výberu.
        onSuccess?.(category.users);
      },
      onError: (e, variables) => {
        logger.error('Error adding user to category', {
          action: 'addUserToCategory',
          error: e,
          data: {
            categoryId,
            users: variables,
          },
        });
        toastError(__('Niečo sa pokazilo'));
      },
    },
  );

  React.useEffect(() => {
    if (isError) {
      toastError(__('Nepodarilo sa načítať používateľov'));
    }
  }, [isError]);

  return (
    <ConfirmationDialog
      open={open}
      confirmLabel={__('Pridať')}
      title={__('Pridanie používateľa do kategórie')}
      formId='addUserForm'
      onClose={onClose}
      fullWidth
      maxWidth='xs'
      isLoading={addReportCategoryUserMutation.isPending}
    >
      <Form
        onSubmit={async (values) => {
          if (!navigator.onLine) {
            toastError(__('Internetové pripojenie zlyhalo.'));
            return;
          }
          try {
            await addReportCategoryUserMutation.mutateAsync(
              Array.isArray(values.assignees)
                ? values.assignees
                : [values.assignees],
            );
          } catch {
            // Empty catch because mutation handles errors
          }
          onClose();
          setAutocompleteValue('');
        }}
        initialValues={{ assignees: [] }}
      >
        {({ handleSubmit, values }) => (
          <form id='addUserForm' onSubmit={handleSubmit}>
            <FormAutocompleteField
              // Pole sa nemenuje `userId`: nad takým názvom vyskočí správca
              // hesiel s ponukou účtov, viď komentár vo `FormAutocompleteField`.
              name='assignees'
              multiple
              options={[
                ...candidates.map(userOption),
                // Už vybrané účty, ktoré aktuálne hľadanie nevrátilo - bez nich
                // by ich značky z poľa zmizli.
                ...((values.assignees as string[]) || [])
                  .filter(
                    (key) =>
                      !candidates.some((user) => candidateKey(user) === key),
                  )
                  .map((key) => seenUsers[key])
                  .filter((user): user is UserCandidate => !!user)
                  .map(userOption),
              ]}
              label={__('Nový používateľ')}
              validate={requiredArray}
              loading={isLoading || isFetchingNextPage}
              disableClientFilter
              hasMore={hasNextPage}
              onLoadMore={fetchNextPage}
              onInputChange={(_event, value, reason) => {
                if (reason === 'input' || reason === 'clear') {
                  setAutocompleteValue(value);
                }
              }}
              noOptionsText={
                isLoading ? __('Používatelia sa načítavajú...') : undefined
              }
            />
          </form>
        )}
      </Form>
    </ConfirmationDialog>
  );
};

export default AddCategoryUserDialog;
